import { useEffect, useState } from 'react';
import { Panel } from '../layout/Panel';
import { ControlTile } from './ControlTile';
import { EmsModeTile } from './EmsModeTile';
import { cancelEvHint, fetchEvHint, setEvHint } from '../../api/evHint';
import {
  cancelTimedForceAction,
  listTimedForceActions,
  postStrategy,
  startTimedForceAction,
} from '../../api/homepageControls';
import type {
  Strategy as ApiStrategy,
  TimedForceAction,
} from '../../api/homepageControls';
import {
  cancelReserveLock,
  fetchReserveLock,
  remainingMs,
  setReserveLock as postReserveLock,
} from '../../api/reserveLock';
import type { ReserveLock, VacationResponse } from '../../api/types';
import { fetchReservationStatus, type ReservationStatus } from '../../api/reservation';
import { fetchVacationState, startVacation, stopVacation } from '../../api/vacation';
import { fmtRemaining } from '../../lib/format';
import { RequestPriority, scheduleRequest } from '../../lib/requestScheduler';
import { visibleInterval } from '../../lib/visibility';
import { engagementToSubline } from '../../lib/engagementCopy';
import { ControlSection } from './ControlSection';
import './controls.css';

interface ControlsColumnProps {
  onLogout?: () => void;
  /** Huidige EMS-mode (uit /api/snapshot) — toont actieve state in EmsModeTile */
  emsMode?: string;
  /** Huidige EMS-strategy (uit /api/snapshot) — als hint onder EmsModeTile */
  emsStrategy?: string;
  /** Huidige ems_action_engagement (uit /api/snapshot) — voor Strategie subline */
  emsEngagement?: string;
  /**
   * Canonieke SOC-floor uit /api/snapshot.floor_canonical_pct (P3-cy194-B, BH-010).
   * Primaire bron voor de floor-display in de Backup-reserve subline.
   * Fallback naar /api/reservation.effective_floor_pct (oudere Pi-builds).
   */
  floorCanonicalPct?: number;
  /**
   * installation.name uit /api/config/installation. Alleen om in de
   * activerings-bevestiging te benoemen welke naam gevraagd wordt; de
   * EmsModeTile vult het invoerveld er niet mee voor.
   */
  siteName?: string;
  /**
   * Heeft de ingelogde gebruiker de admin-rol (uit /api/auth/status)?
   *
   * Punt 63: `POST /api/controls/force/timed` is server-side van
   * SELF_SERVICE naar ADMIN gegaan. Die route schrijft `ems.strategy=manual`
   * plus een vast vermogenssetpoint — hetzelfde wat de admin-only
   * override-route schrijft — dus een niet-admin bereikte er wat hem elders
   * geweigerd werd.
   *
   * Alleen het STARTEN van een force-actie hangt hiervan af. Annuleren blijft
   * voor iedereen open: `DELETE /api/controls/force/timed/{force_id}` is
   * bewust SELF_SERVICE gebleven, want wie iets hoort gebeuren moet het
   * kunnen stoppen. De overige tegels in deze kolom (laadpaal, reservering,
   * vakantie) blijven eveneens SELF_SERVICE.
   *
   * Onbekend (undefined) telt als geen admin: dat is de kant waar een
   * vergissing alleen een knop kost en geen ongevraagde setpoint.
   */
  isAdmin?: boolean;
}

// F-H1 audit cycle #14 — vóór: alle non-EV/Reserve controls waren STUBS die
// alleen naar console.log schreven. Operator klikte en dacht actie was
// uitgevoerd. Nu wired:
// - Strategie buttons → /api/admin/override-bulk (ems.strategy) + restart
// - Force-actie (Laad/Ontlaad nu) → /api/admin/override-bulk (manual + setpoint)
// - Manual Toepassen + Vacation: GEEN backend pad, knoppen disabled met tooltip
//   "binnenkort — vereist backend-endpoint" zodat operator niet misleid wordt.

type Strategy = 'revenue_max' | 'self_use' | 'manual';
type ManualAction = 'idle' | 'charge' | 'discharge';
type EvState = 'off' | 'today';
type EvSize = 'klein' | 'mid' | 'groot';

const EV_SIZE_LABELS: Record<EvSize, string> = {
  klein: 'Klein',
  mid: 'Mid',
  groot: 'Groot',
};
const EV_N_OPTIONS = [1, 2, 3];

const STRATEGY_LABELS: Record<Strategy, string> = {
  revenue_max: 'Revenue Max',
  self_use: 'Self-use',
  manual: 'Manual',
};

// Vermogens in kW — covers inverter range (50K) in zinvolle stappen.
const POWER_KW = [5, 10, 15, 20, 25, 30, 40, 50];
const FORCE_HOURS = [1, 2, 3, 4, 6, 8];
// EV: SOC-leeg-percentage in stappen van 10%; thuis-uur in hele uren.
const EV_EMPTY_SOC = [10, 20, 30, 40, 50, 60, 70, 80, 90];
const EV_HOURS = Array.from({ length: 19 }, (_, i) => i + 6); // 06:00 - 24:00
const RESERVE_HOURS = [12, 24, 48, 72, 168]; // 12u, 24u, 2d, 3d, 1week

function fmtHours(h: number): string {
  if (h < 24) return `${h}u`;
  const days = h / 24;
  return days === 1 ? '24u' : `${days}d`;
}

const RESERVE_SOC_STEPS = [10, 20, 30, 40, 50, 60, 70, 80, 90];

// Vacation: backend valideert 5..20. Standaard opties dekken alle zinvolle floor-niveaus.
const VACATION_SOC_OPTIONS = [5, 10, 15, 20];
// Polling-interval voor /api/controls/vacation (consistent met reserve-lock).
const VACATION_POLL_MS = 30_000;

/** Format DD-MM uit een ISO date/datetime string. Returns '—' bij parse-fout. */
function fmtEndDate(isoStr: string | null): string {
  if (!isoStr) return '—';
  const d = new Date(isoStr);
  if (Number.isNaN(d.getTime())) return '—';
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  return `${dd}-${mm}`;
}

/** Vandaag + n dagen → YYYY-MM-DD lokaal (zonder TZ-shift via toISOString). */
function todayPlusDays(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() + n);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

export function ControlsColumn({ onLogout, emsMode, emsStrategy, emsEngagement, floorCanonicalPct, siteName, isAdmin }: ControlsColumnProps = {}) {
  // Zie de prop-documentatie: het starten van een force-actie vereist de
  // admin-rol, het annuleren niet.
  const mayStartForce = isAdmin === true;
  // De strategieknoppen schrijven via /api/admin/override-bulk, en die staat
  // op ADMIN — al vóór punt #63, dus dit was een stille 403 en geen regressie
  // van die wijziging. Voor de bewoner is het verschil er niet: een knop die
  // pas bij het indrukken weigert is een storing.
  const maySetStrategy = isAdmin === true;
  const [strategy, setStrategy] = useState<Strategy>('revenue_max');
  const [manualAction, setManualAction] = useState<ManualAction>('idle');
  const [manualPowerKw, setManualPowerKw] = useState<number>(20);
  const [forcePowerKw, setForcePowerKw] = useState<number>(10);
  const [forceHours, setForceHours] = useState<number>(1);
  // F-H1 audit cycle #14 — busy/error/success state voor Strategy + Force-actie.
  // Geen toast-systeem aanwezig (zie audit-doc); tile-inline status volstaat.
  const [strategyBusy, setStrategyBusy] = useState<boolean>(false);
  const [strategyError, setStrategyError] = useState<string | null>(null);
  const [strategyOk, setStrategyOk] = useState<string | null>(null);
  const [forceBusy, setForceBusy] = useState<boolean>(false);
  const [forceError, setForceError] = useState<string | null>(null);
  const [forceOk, setForceOk] = useState<string | null>(null);
  // R&D cycle #15: actieve force-action met countdown — poll elke 5s.
  const [forceActive, setForceActive] = useState<TimedForceAction | null>(null);
  const [ev, setEv] = useState<EvState>('off');
  const [evEmptyPct, setEvEmptyPct] = useState<number>(40);
  const [evHour, setEvHour] = useState<number>(18);
  const [evNEvs, setEvNEvs] = useState<number>(1);
  const [evSize, setEvSize] = useState<EvSize>('mid');
  const [evError, setEvError] = useState<string | null>(null);
  const [evBusy, setEvBusy] = useState<boolean>(false);

  // Bij mount: lees actieve hint uit /api/controls/ev en sync UI-state.
  // Via de burst-scheduler (perf 2026-08-11): de tegel-state van deze kolom
  // mag wachten tot de hoofdgrafiek zijn data heeft.
  useEffect(() => {
    let cancelled = false;
    scheduleRequest(fetchEvHint, RequestPriority.controls)
      .then((hint) => {
        if (cancelled || !hint) return;
        setEv('today');
        setEvEmptyPct(hint.empty_pct);
        setEvHour(hint.expected_home_hour);
        if (typeof hint.n_evs === 'number') setEvNEvs(hint.n_evs);
        if (hint.size === 'klein' || hint.size === 'mid' || hint.size === 'groot') {
          setEvSize(hint.size);
        }
      })
      .catch(() => {
        // GET fail = niet kritiek, gewoon default
      });
    return () => { cancelled = true; };
  }, []);

  async function handleEvToggle() {
    const next: EvState = ev === 'today' ? 'off' : 'today';
    setEvBusy(true);
    setEvError(null);
    try {
      if (next === 'today') {
        await setEvHint(evEmptyPct, evHour, { nEvs: evNEvs, size: evSize });
      } else {
        await cancelEvHint();
      }
      setEv(next);
    } catch (err) {
      setEvError((err as Error).message.slice(0, 80));
    } finally {
      setEvBusy(false);
    }
  }

  async function handleStrategyChange(next: Strategy) {
    // Optimistic: update lokale UI direct, server-restart duurt ~3s.
    setStrategy(next);
    setStrategyBusy(true);
    setStrategyError(null);
    setStrategyOk(null);
    try {
      await postStrategy(next as ApiStrategy);
      setStrategyOk(`OK · ${STRATEGY_LABELS[next]}`);
      // Clear success-bericht na 5s zodat tile-status terug naar default kan.
      window.setTimeout(() => setStrategyOk(null), 5_000);
    } catch (err) {
      setStrategyError((err as Error).message.slice(0, 60));
    } finally {
      setStrategyBusy(false);
    }
  }

  async function handleForceAction(direction: 'charge' | 'discharge') {
    setForceBusy(true);
    setForceError(null);
    setForceOk(null);
    try {
      const created = await startTimedForceAction(direction, forcePowerKw, forceHours);
      setForceActive(created);
      setForceOk(
        `OK · ${direction === 'charge' ? 'laad' : 'ontlaad'} ${forcePowerKw} kW · ${forceHours}u`,
      );
      // Lokale state: strategy ging naar 'manual' tot auto-revert.
      setStrategy('manual');
      window.setTimeout(() => setForceOk(null), 5_000);
    } catch (err) {
      setForceError((err as Error).message.slice(0, 60));
    } finally {
      setForceBusy(false);
    }
  }

  // R&D cycle #15: bij mount + elke 5s pollen → toon countdown bij actieve actie.
  useEffect(() => {
    let cancelled = false;
    // Alleen de eerste ronde loopt via de burst-scheduler; het 5s-interval
    // daarna blijft ongewijzigd doorlopen.
    let first = true;
    const refresh = () => {
      const call = first
        ? scheduleRequest(listTimedForceActions, RequestPriority.controls)
        : listTimedForceActions();
      first = false;
      call
        .then((rows) => {
          if (cancelled) return;
          setForceActive(rows.length > 0 ? rows[0] : null);
        })
        .catch(() => {
          // GET fail = niet kritiek (auth nog niet geladen) — UI valt
          // terug op idle-state, gebruiker kan opnieuw proberen.
        });
    };
    refresh();
    // Pauzeert bij een verborgen tab — dit is met 5s de snelste poller van de
    // hele kolom. Zie lib/visibility (auditbevinding #69).
    const stop = visibleInterval(refresh, 5_000);
    return () => {
      cancelled = true;
      stop();
    };
  }, []);

  async function handleForceCancel() {
    if (!forceActive) return;
    setForceBusy(true);
    setForceError(null);
    setForceOk(null);
    try {
      await cancelTimedForceAction(forceActive.id);
      setForceActive(null);
      setForceOk('OK · geannuleerd + gerevert');
      window.setTimeout(() => setForceOk(null), 5_000);
    } catch (err) {
      setForceError((err as Error).message.slice(0, 60));
    } finally {
      setForceBusy(false);
    }
  }

  function fmtCountdown(seconds: number): string {
    if (seconds <= 0) return '0m';
    const totalMin = Math.floor(seconds / 60);
    if (totalMin < 60) return `${totalMin}m`;
    const h = Math.floor(totalMin / 60);
    const m = totalMin % 60;
    return m === 0 ? `${h}u` : `${h}u${m}m`;
  }
  const [reserveSoc, setReserveSoc] = useState<number>(20);
  const [reserveHours, setReserveHours] = useState<number>(24);
  const [reserveLock, setReserveLockState] = useState<ReserveLock | null>(null);
  const [reserveBusy, setReserveBusy] = useState<boolean>(false);
  const [reserveError, setReserveError] = useState<string | null>(null);
  const [reserveCountdownMs, setReserveCountdownMs] = useState<number>(0);
  const [reserveSafetyFloor, setReserveSafetyFloor] = useState<number>(10);

  // Backup-reserve subline: huidige dynamic floor uit /api/reservation. Polling
  // 30s — matched de cadence van reserve-lock + vacation. Geen failure-mode:
  // bij fail blijft reservationStatus null → subline rendert niet.
  const [reservationStatus, setReservationStatus] = useState<ReservationStatus | null>(null);
  useEffect(() => {
    let cancelled = false;
    let first = true;
    const load = () => {
      const call = first
        ? scheduleRequest(fetchReservationStatus, RequestPriority.secondary)
        : fetchReservationStatus();
      first = false;
      call
        .then((r) => { if (!cancelled) setReservationStatus(r); })
        .catch(() => { /* niet kritiek */ });
    };
    load();
    const stop = visibleInterval(load, 30_000);
    return () => { cancelled = true; stop(); };
  }, []);

  // Mount: lees server-state. Bij actieve lock: sync UI-input naar lock-waarden
  // zodat gebruiker direct ziet wat er actief is en de input-state matcht
  // wanneer ze annuleert + opnieuw activeert.
  useEffect(() => {
    let cancelled = false;
    // Deelt zijn request met de poll in App.tsx (zie api/reserveLock.ts).
    scheduleRequest(fetchReserveLock, RequestPriority.controls)
      .then((res) => {
        if (cancelled) return;
        setReserveSafetyFloor(res.safety_floor_pct);
        if (res.lock) {
          setReserveLockState(res.lock);
          setReserveSoc(res.lock.soc_pct);
          setReserveHours(res.lock.duration_hours);
          setReserveCountdownMs(remainingMs(res.lock));
        }
      })
      .catch(() => {
        // GET fail = niet kritiek (auth nog niet geladen, etc.) — defaults blijven
      });
    return () => { cancelled = true; };
  }, []);

  // Countdown-ticker — alleen actief als lock leeft. 30s interval is genoeg
  // (UI granularity = minuten). Bij ms=0 cleart UI direct; backend cleart vanzelf
  // bij volgende load_lock-call.
  useEffect(() => {
    if (!reserveLock) return undefined;
    const tick = () => {
      const ms = remainingMs(reserveLock);
      setReserveCountdownMs(ms);
      if (ms === 0) setReserveLockState(null);
    };
    tick();
    const id = setInterval(tick, 30_000);
    return () => clearInterval(id);
  }, [reserveLock]);

  async function handleReserveToggle() {
    setReserveBusy(true);
    setReserveError(null);
    try {
      if (reserveLock) {
        await cancelReserveLock();
        setReserveLockState(null);
        setReserveCountdownMs(0);
      } else {
        const lock = await postReserveLock(reserveSoc, reserveHours);
        setReserveLockState(lock);
        setReserveCountdownMs(remainingMs(lock));
      }
    } catch (err) {
      setReserveError((err as Error).message.slice(0, 80));
    } finally {
      setReserveBusy(false);
    }
  }

  // Vacation: end-date default = vandaag + 14 dagen (typische 2w-trip).
  // soc_min default 5% (laagste toegestane = max arbitrage-ruimte).
  const [vacationEndDate, setVacationEndDate] = useState<string>(() => todayPlusDays(14));
  const [vacationSocPct, setVacationSocPct] = useState<number>(5);
  const [vacationState, setVacationState] = useState<VacationResponse | null>(null);
  const [vacationBusy, setVacationBusy] = useState<boolean>(false);
  const [vacationError, setVacationError] = useState<string | null>(null);

  // Mount + 30s poll voor /api/controls/vacation. Een auto-stop op de engine
  // (ends_at_utc verlopen → cleanup tijdens tick) is dan binnen 30s zichtbaar
  // in de UI zonder reload.
  useEffect(() => {
    let cancelled = false;
    let first = true;
    const load = () => {
      const call = first
        ? scheduleRequest(fetchVacationState, RequestPriority.secondary)
        : fetchVacationState();
      first = false;
      call
        .then((res) => { if (!cancelled) setVacationState(res); })
        .catch(() => { /* niet kritiek */ });
    };
    load();
    const stop = visibleInterval(load, VACATION_POLL_MS);
    return () => { cancelled = true; stop(); };
  }, []);

  async function handleVacationStart() {
    setVacationBusy(true);
    setVacationError(null);
    try {
      const res = await startVacation({
        endDate: vacationEndDate,
        socMinPct: vacationSocPct,
      });
      setVacationState(res);
    } catch (err) {
      setVacationError((err as Error).message.slice(0, 100));
    } finally {
      setVacationBusy(false);
    }
  }

  async function handleVacationStop() {
    setVacationBusy(true);
    setVacationError(null);
    try {
      await stopVacation();
      setVacationState({
        active: false,
        started_at_utc: null,
        ends_at_utc: null,
        soc_min_override_pct: null,
        soc_min_pre_state_pct: null,
        days_remaining: null,
        actor: null,
      });
    } catch (err) {
      setVacationError((err as Error).message.slice(0, 100));
    } finally {
      setVacationBusy(false);
    }
  }

  const vacationActive = vacationState?.active === true;
  const vacationStatus = vacationError
    ? `! ${vacationError}`
    : vacationActive
      ? `tot ${fmtEndDate(vacationState?.ends_at_utc ?? null)} · ` +
        `nog ${Math.max(0, Math.round(vacationState?.days_remaining ?? 0))}d`
      : 'Uit';

  const reserveActive = reserveLock !== null;
  const reserveStatus = reserveError
    ? `! ${reserveError}`
    : reserveActive
      ? `${reserveLock.soc_pct}% · nog ${fmtRemaining(reserveCountdownMs)}`
      : `${reserveSoc}% · ${fmtHours(reserveHours)}`;
  const reserveSocOptions = RESERVE_SOC_STEPS.filter((p) => p >= reserveSafetyFloor);

  // F-H1 audit cycle #14 — status-tekst bevat success/error inline zodat
  // operator binnen 0.5s feedback krijgt na een klik.
  const strategyStatus = strategyError
    ? `! ${strategyError}`
    : strategyOk
      ? strategyOk
      : strategy === 'manual'
        ? `Manual · ${manualAction}${manualAction !== 'idle' ? ` ${manualPowerKw} kW` : ''}`
        : STRATEGY_LABELS[strategy];
  const manualEnabled = strategy === 'manual';
  const forceStatus = forceError
    ? `! ${forceError}`
    : forceActive
      ? `${forceActive.action === 'charge' ? 'Laad' : 'Ontlaad'} ${forceActive.power_kw} kW · nog ${fmtCountdown(forceActive.countdown_seconds ?? 0)}`
      : forceOk
        ? forceOk
        : `${forcePowerKw} kW · ${forceHours}u`;

  // BH-010 / P3-cy194-B: floor-display prioriteit:
  //   1. floorCanonicalPct prop (snapshot.floor_canonical_pct, server-side canonical)
  //   2. reservationStatus.effective_floor_pct (oudere Pi-builds zonder snapshot-veld)
  //   3. reservationStatus.current_floor_pct (oudste builds zonder effective_floor_pct)
  // De canonieke waarde is server-side max(static, current) en de grondwaarheid
  // voor de EMS mode-gate. Fallback-keten garandeert backward-compat.
  const effectiveFloorPct =
    floorCanonicalPct ??
    reservationStatus?.effective_floor_pct ??
    reservationStatus?.current_floor_pct;
  const backupSubline = effectiveFloorPct != null || reservationStatus
    ? reserveActive
      ? `Lock overschrijft dyn. floor ${(effectiveFloorPct ?? 0).toFixed(0)}%`
      : reservationStatus
        ? `Dyn. floor ${(effectiveFloorPct ?? 0).toFixed(0)}% (rolling ${reservationStatus.horizon_hours}h)`
        : `Dyn. floor ${(effectiveFloorPct ?? 0).toFixed(0)}%`
    : null;

  const strategySubline = engagementToSubline(emsEngagement);

  // Task 5/15 — herstructurering naar 3 ControlSections (2026-05-18):
  // Sectie 1 (Modus & Veiligheid):   EMS-modus · Backup-reserve
  // Sectie 2 (Strategie & Planning): Strategie · EV-mode
  // Sectie 3 (Directe acties):       Force-actie · Vacation (verhuisd van rij 2)
  //
  // Rationale: semantische groepering ipv positiegrids. Vacation hoort bij
  // "direct ageren op situatie" ipv "planning" — verplaatst naar sectie 3.

  return (
    <Panel className="panel-controls">
      <ControlSection title="Modus & Veiligheid">
        <EmsModeTile
          currentMode={emsMode}
          siteName={siteName}
          isAdmin={isAdmin}
          hint={
            emsMode === 'active'
              ? `actief · strategie ${emsStrategy ?? '—'}`
              : 'shadow / geen writes'
          }
        />
        <ControlTile
          label="Backup-reserve"
          status={reserveStatus}
          accent="#22c55e"
          subline={backupSubline}
          hint={reserveActive
            ? 'Lock actief — "Annuleer" om terug te vallen op dynamische floor'
            : 'SOC-floor + hoe lang reserveren'}
        >
          <div className="ctl-select-pair">
            <div className="ctl-select-group">
              <span className="ctl-select-mini-label">SOC-floor</span>
              <select
                className="ctl-select"
                value={reserveSoc}
                disabled={reserveActive || reserveBusy}
                onChange={(e) => setReserveSoc(Number(e.target.value))}
              >
                {reserveSocOptions.map((p) => <option key={p} value={p}>{p}%</option>)}
              </select>
            </div>
            <div className="ctl-select-group">
              <span className="ctl-select-mini-label">Duur</span>
              <select
                className="ctl-select"
                value={reserveHours}
                disabled={reserveActive || reserveBusy}
                onChange={(e) => setReserveHours(Number(e.target.value))}
              >
                {RESERVE_HOURS.map((h) => <option key={h} value={h}>{fmtHours(h)}</option>)}
              </select>
            </div>
          </div>
          <button
            className={`ctl-btn${reserveActive ? ' active' : ''}`}
            disabled={reserveBusy}
            onClick={handleReserveToggle}
          >{reserveBusy ? '...' : reserveActive ? 'Annuleer' : 'Activeer lock'}</button>
        </ControlTile>
      </ControlSection>

      <ControlSection title="Strategie & Planning">
        <ControlTile
          label="Strategie"
          status={strategyStatus}
          accent="#6366f1"
          subline={strategySubline}
          hint={strategyBusy ? 'Bezig met opslaan + restart loop (~3s)…' : undefined}
        >
          <div className="ctl-segment">
            <button
              className={`ctl-btn${strategy === 'revenue_max' ? ' active' : ''}`}
              disabled={strategyBusy || !maySetStrategy}
              onClick={() => handleStrategyChange('revenue_max')}
            >Rev. Max</button>
            <button
              className={`ctl-btn${strategy === 'self_use' ? ' active' : ''}`}
              disabled={strategyBusy || !maySetStrategy}
              onClick={() => handleStrategyChange('self_use')}
            >Self-use</button>
            <button
              className={`ctl-btn${strategy === 'manual' ? ' active' : ''}`}
              disabled={strategyBusy || !maySetStrategy}
              onClick={() => handleStrategyChange('manual')}
            >Manual</button>
          </div>
          {!maySetStrategy && (
            <div className="ctl-note" data-testid="strategy-admin-note">
              De strategie omzetten vereist de beheerdersrol. Je ziet wel welke
              strategie nu draait.
            </div>
          )}
          {manualEnabled && (
            <div className="control-row">
              <select
                className="ctl-select"
                value={manualAction}
                onChange={(e) => setManualAction(e.target.value as ManualAction)}
              >
                <option value="idle">Idle</option>
                <option value="charge">Charge</option>
                <option value="discharge">Discharge</option>
              </select>
              <select
                className="ctl-select"
                disabled={manualAction === 'idle'}
                value={manualPowerKw}
                onChange={(e) => setManualPowerKw(Number(e.target.value))}
              >
                {POWER_KW.map((p) => <option key={p} value={p}>{p} kW</option>)}
              </select>
            </div>
          )}
          {manualEnabled && (
            // F-H1 audit cycle #14: Manual Toepassen is een placeholder zonder
            // backend-endpoint (zou /api/controls/manual nodig hebben). Toon
            // dit als info-tekst ipv onbruikbare knop — operator wordt naar
            // Force-actie verwezen voor directe charge/discharge dispatch.
            <p className="control-placeholder">
              Toepassen niet beschikbaar — gebruik <strong>Force-actie</strong> voor directe charge/discharge.
            </p>
          )}
        </ControlTile>

        <ControlTile
          label="EV-mode"
          status={
            evError
              ? `! ${evError}`
              : ev === 'off'
                ? 'Uit'
                : `${evNEvs}× ${EV_SIZE_LABELS[evSize]} · ${evEmptyPct}% · ${String(evHour).padStart(2, '0')}:00`
          }
          accent="#0ea5e9"
          hint="Aantal + grootte EV(s), gemiddeld % leeg en thuis-uur (lokaal)"
        >
          <div className="ctl-select-pair">
            <div className="ctl-select-group">
              <span className="ctl-select-mini-label">Aantal EV's</span>
              <select
                className="ctl-select"
                value={evNEvs}
                onChange={(e) => setEvNEvs(Number(e.target.value))}
                disabled={evBusy}
              >
                {EV_N_OPTIONS.map((n) => <option key={n} value={n}>{n}×</option>)}
              </select>
            </div>
            <div className="ctl-select-group">
              <span className="ctl-select-mini-label">Grootte</span>
              <select
                className="ctl-select"
                value={evSize}
                onChange={(e) => setEvSize(e.target.value as EvSize)}
                disabled={evBusy}
              >
                <option value="klein">Klein</option>
                <option value="mid">Mid</option>
                <option value="groot">Groot</option>
              </select>
            </div>
          </div>
          <div className="ctl-select-pair">
            <div className="ctl-select-group">
              <span className="ctl-select-mini-label">SOC bij thuis</span>
              <select
                className="ctl-select"
                value={evEmptyPct}
                onChange={(e) => setEvEmptyPct(Number(e.target.value))}
                disabled={evBusy}
              >
                {EV_EMPTY_SOC.map((s) => <option key={s} value={s}>{s}% leeg</option>)}
              </select>
            </div>
            <div className="ctl-select-group">
              <span className="ctl-select-mini-label">Thuis vanaf</span>
              <select
                className="ctl-select"
                value={evHour}
                onChange={(e) => setEvHour(Number(e.target.value))}
                disabled={evBusy}
              >
                {EV_HOURS.map((h) => <option key={h} value={h}>{String(h).padStart(2, '0')}:00</option>)}
              </select>
            </div>
          </div>
          <button
            className={`ctl-btn${ev === 'today' ? ' active' : ''}`}
            disabled={evBusy}
            onClick={handleEvToggle}
          >{evBusy ? '...' : ev === 'today' ? 'Annuleer' : 'Plan EV-laden vandaag'}</button>
        </ControlTile>
      </ControlSection>

      <ControlSection title="Directe acties">
        <ControlTile
          label="Force actie"
          status={forceStatus}
          accent="#f59e0b"
          hint={
            forceBusy
              ? 'Bezig met opslaan + restart (~3s)…'
              : forceActive
                ? `Auto-revert naar ${forceActive.revert_to_strategy} bij expiry. "Annuleer" stopt direct.`
                : 'Zet manual + fixed setpoint. Auto-revert naar huidige strategie na de gekozen duur.'
          }
        >
          <div className="ctl-select-pair">
            <div className="ctl-select-group">
              <span className="ctl-select-mini-label">Vermogen</span>
              <select
                className="ctl-select"
                value={forcePowerKw}
                disabled={forceBusy || forceActive !== null || !mayStartForce}
                onChange={(e) => setForcePowerKw(Number(e.target.value))}
              >
                {POWER_KW.map((p) => <option key={p} value={p}>{p} kW</option>)}
              </select>
            </div>
            <div className="ctl-select-group">
              <span className="ctl-select-mini-label">Duur</span>
              <select
                className="ctl-select"
                value={forceHours}
                disabled={forceBusy || forceActive !== null || !mayStartForce}
                onChange={(e) => setForceHours(Number(e.target.value))}
                title="Auto-revert na deze duur"
              >
                {FORCE_HOURS.map((h) => <option key={h} value={h}>{h}u</option>)}
              </select>
            </div>
          </div>
          {forceActive ? (
            <div className="control-row">
              <button
                className="ctl-btn active"
                disabled={forceBusy}
                onClick={handleForceCancel}
              >Annuleer</button>
            </div>
          ) : (
            <>
              <div className="control-row">
                <button
                  className="ctl-btn"
                  disabled={forceBusy || !mayStartForce}
                  onClick={() => handleForceAction('charge')}
                >Laad</button>
                <button
                  className="ctl-btn"
                  disabled={forceBusy || !mayStartForce}
                  onClick={() => handleForceAction('discharge')}
                >Ontlaad</button>
              </div>
              {!mayStartForce && (
                // Uitgeschakeld en niet verborgen: de operator ziet dat de
                // functie bestaat en waarom hij er niet bij kan, in plaats van
                // een knop die pas bij het indrukken een 403 blijkt te geven.
                <div className="ctl-note" data-testid="force-admin-note">
                  Een force-actie starten vereist de beheerdersrol. Een lopende
                  actie annuleren kan wel.
                </div>
              )}
            </>
          )}
        </ControlTile>

        <ControlTile
          label="Vacation mode"
          status={vacationStatus}
          accent="#a855f7"
          hint={vacationActive
            ? `Lagere floor ${vacationState?.soc_min_override_pct ?? '—'}% — "Stop" reverteert naar ${vacationState?.soc_min_pre_state_pct ?? '—'}%`
            : 'Verlaag SOC-floor tijdelijk voor meer arbitrage-ruimte tijdens afwezigheid (1-60 dagen)'}
        >
          {vacationActive ? (
            // Active state: status + Stop button. Geen edit van end-date /
            // soc_min mogelijk — operator moet eerst stoppen, dan opnieuw
            // starten met nieuwe waarden (anders zou pre-state gecorrumpeerd
            // raken bij in-place edit).
            <button
              className="ctl-btn active"
              disabled={vacationBusy}
              onClick={handleVacationStop}
              data-testid="vacation-stop-btn"
            >{vacationBusy ? '...' : 'Stop vacation'}</button>
          ) : (
            <>
              <div className="ctl-select-pair">
                <div className="ctl-select-group">
                  <span className="ctl-select-mini-label">Tot datum</span>
                  <input
                    type="date"
                    className="ctl-select"
                    value={vacationEndDate}
                    min={todayPlusDays(1)}
                    max={todayPlusDays(60)}
                    disabled={vacationBusy}
                    onChange={(e) => setVacationEndDate(e.target.value)}
                    data-testid="vacation-end-date"
                  />
                </div>
                <div className="ctl-select-group">
                  <span className="ctl-select-mini-label">SOC-floor</span>
                  <select
                    className="ctl-select"
                    value={vacationSocPct}
                    disabled={vacationBusy}
                    onChange={(e) => setVacationSocPct(Number(e.target.value))}
                    data-testid="vacation-soc-pct"
                  >
                    {VACATION_SOC_OPTIONS.map((p) => (
                      <option key={p} value={p}>{p}% floor</option>
                    ))}
                  </select>
                </div>
              </div>
              <button
                className="ctl-btn"
                disabled={vacationBusy || !vacationEndDate}
                onClick={handleVacationStart}
                data-testid="vacation-start-btn"
              >{vacationBusy ? '...' : 'Start vacation'}</button>
            </>
          )}
        </ControlTile>
      </ControlSection>

      {/* Uitloggen-knop verhuisd naar FlowSVG header (naast "Laatste update").
          onLogout-prop blijft beschikbaar voor backwards-compat. */}
      {onLogout && (
        <button
          type="button"
          className="ctl-logout"
          onClick={onLogout}
          style={{ display: 'none' }}
        >Uitloggen</button>
      )}
    </Panel>
  );
}
